import {
  AccessibleOutlined,
  EmailOutlined,
  HomeOutlined,
  Phone,
  SecurityOutlined,
} from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Grid, Paper, Typography } from '@mui/material';
import { Lock } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import CustomButton from '../../common/button/commonButton';
import HospitalDialog from '../../common/dialog/commonDialogBox';
import { APP_LINK } from '../../common/menu/menuItems/menuList';
import ReusableFormFields from '../../common/textfield/ReusableTextfield';
import { styles } from '../../commonStyle/formStyles';
import { fetchBlocks } from '../../redux/block/blockAction';
import { fetchFloors } from '../../redux/floor/floorAction';
import {
  fetchSecurityOfficer,
  resetCurrentSecurityOfficer,
  updateSecurityOfficer,
} from '../../redux/secuityOfficer/securityAction';
import { fetchWards } from '../../redux/ward/wardAction';

import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
function EditSecurityGuardForm() {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    reset,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { id } = useParams();

  useEffect(() => {
    dispatch(fetchSecurityOfficer(id));
  }, [dispatch, id]);

  const SecurityOfficerState = useSelector(
    (state) => state?.securityOfficer?.fetchSecurityOfficer.data || {}
  );
  const blockList = useSelector((state) => state?.block?.fetchBlocks || {});
  const floorList = useSelector((state) => state?.floor?.fetchFloors || {});
  const wardList = useSelector((state) => state?.ward?.fetchWards || {});
  const storedBlock = localStorage.getItem('block') || '';
  const storedFloor = localStorage.getItem('floor') || '';
  const getBlockOptions = () => {
    return (
      blockList?.Blocks?.data
        ?.filter((block) => block.block_status !== 2)
        .map((block) => ({
          value: block.id,
          label: block.block_name,
        })) || []
    );
  };

  const getFloorOptions = () => {
    return (
      floorList?.floors?.data
        ?.filter((floor) => floor.floor_status !== 0)
        .map((floor) => ({
          value: floor.id,
          label: floor.floor_name,
        })) || []
    );
  };

  const getWardOptions = () => {
    if (wardList?.wards?.data) {
      return wardList.wards.data
        .filter((ward) => ward.ward_status !== 0)
        .map((ward) => ({
          value: ward.id,
          label: ward.ward_name,
        }));
    }
    return [];
  };
  const watchBlock = watch('block_id');
  const watchFloor = watch('floor_id');
  const watchWard = watch('ward_id');
  useEffect(() => {
    if (watchBlock) {
      localStorage.setItem('block', watchBlock);
    }
    if (watchFloor) {
      localStorage.setItem('floor', watchFloor);
    }
    if (watchWard) {
      localStorage.setItem('ward', watchWard);
    }
  }, [watchBlock, watchFloor]);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchBlocks());
      if (storedBlock) {
        dispatch(fetchFloors(storedBlock));

        if (storedFloor) {
          dispatch(fetchWards(storedBlock, storedFloor));
        }
      }
    };
    fetchData();
  }, [dispatch, storedBlock, storedFloor]);

  useEffect(() => {
    if (watchBlock) {
      dispatch(fetchFloors(watchBlock));
      if (watchBlock !== storedBlock) {
        setValue('floor_id', '');
        setValue('ward_id', '');
      }
    }
  }, [watchBlock, dispatch, setValue, storedBlock]);

  useEffect(() => {
    if (watchBlock && watchFloor) {
      dispatch(fetchWards(watchBlock, watchFloor));
      if (watchFloor !== storedFloor) {
        setValue('ward_id', '');
      }
    }
  }, [watchBlock, watchFloor, dispatch, setValue, storedFloor]);

  useEffect(() => {
    if (SecurityOfficerState) {
      setValue(
        'security_gaurd_name',
        SecurityOfficerState.security_gaurd_name || ''
      );
      setValue('email', SecurityOfficerState.email || '');
      setValue('phone_number', SecurityOfficerState.phone_number || '');
      setValue('address', SecurityOfficerState.address || '');
      setValue(
        'security_license_number',
        SecurityOfficerState.security_license_number || ''
      );
      setValue('status', SecurityOfficerState.status || '');
      setValue('block_id', SecurityOfficerState.block_id);
      setValue('floor_id', SecurityOfficerState.floor_id);
      setValue('ward_id', SecurityOfficerState.ward_id);
    }
  }, [SecurityOfficerState, setValue]);

  const securityState = useSelector(
    (state) => state?.securityOfficer?.updateSecurityOfficer || {}
  );
  useEffect(() => {
    clearErrors();

    if (securityState.error && securityState.errorMessage) {
      if (securityState.errorMessage === 'email must be unique') {
        setError('email', {
          type: 'manual',
          message: 'Email already exists',
        });
      } else if (securityState.errorMessage === 'phone must be unique') {
        setError('phone_number', {
          type: 'manual',
          message: 'Phone number already exists',
        });
      } else {
        setDialogMessage(securityState.errorMessage);
        setIsSuccess(false);
        setOpenDialog(true);
      }
      // dispatch(resetCurrentSecurityOfficer());
    } else if (securityState?.success) {
      setDialogMessage({
        title: 'Success',
        message: 'Security Updated  successfully.',
        type: 'success',
      });
      setIsSuccess(true);
      setOpenDialog(true);
      setTimeout(() => {
        reset();
      }, 1000);
      dispatch(resetCurrentSecurityOfficer());
    }
  }, [securityState, setError, clearErrors, reset]);

  const inputFields = [
    {
      name: 'security_gaurd_name',
      label: 'Security Guard Name',
      placeholder: 'Enter name',
      type: 'text',
      tabIndex: 1,
      rules: { required: 'Name is required' },
      startIcon: <SecurityOutlined />,
    },
    {
      name: 'email',
      label: 'Email',
      placeholder: 'Enter email',
      type: 'email',
      tabIndex: 2,
      rules: {
        required: 'Email is required',
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: 'Invalid email address',
        },
      },
      startIcon: <EmailOutlined />,
    },
    {
      name: 'phone_number',
      label: 'Phone Number',
      placeholder: 'Enter phone number',
      type: 'tel',
      tabIndex: 3,
      maxLength: 10,
      showCharCount: false,
      rules: {
        required: 'Phone number is required',
        pattern: {
          value: /^(6|7|8|9)\d{9}$/,
          message: 'Invalid phone number',
        },
        maxLength: {
          value: 10,
          message: 'Phone number should be 10 digits',
        },
      },
      startIcon: <Phone />,
    },
    {
      name: 'address',
      label: 'Address',
      placeholder: 'Enter address',
      type: 'text',
      tabIndex: 4,
      rules: { required: 'Address is required' },
      startIcon: <HomeOutlined />,
    },
    {
      name: 'security_license_number',
      label: 'Security License Number',
      placeholder: 'Enter license number',
      type: 'text',
      tabIndex: 5,
      rules: { required: 'Security License Number is required' },
      startIcon: <SecurityOutlined />,
    },
    {
      name: 'password',
      label: 'Password',
      placeholder: 'Enter password',
      type: 'password',
      tabIndex: 6,
      maxLength: 10,
      showCharCount: false,
      rules: {
        minLength: {
          value: 10,
          message: 'Password must be exactly 10 characters',
        },
        maxLength: {
          value: 10,
          message: 'Password must be exactly 10 characters',
        },
      },
      startIcon: <Lock />,
    },
    {
      name: 'block_id',
      label: 'Block',
      placeholder: 'Select block',
      type: 'select',
      tabIndex: 7,
      options: getBlockOptions(),
      rules: { required: 'Block is required' },
      startIcon: <LocalHospitalIcon />,
    },
    // {
    //   name: 'floor_id',
    //   label: 'Floor',
    //   placeholder: 'Select Floor',
    //   type: 'select',
    //   tabIndex: 8,
    //   options: getFloorOptions(),
    //   rules: { required: 'Floor is required' },
    //   startIcon: <ApartmentIcon />,
    //   // disabled: !watchBlock,
    // },
    // {
    //   name: 'ward_id',
    //   label: 'Ward Name',
    //   placeholder: 'Select ward name',
    //   type: 'select',
    //   tabIndex: 9,
    //   options: getWardOptions(),
    //   rules: { required: 'Ward name is required' },
    //   startIcon: <MeetingRoomIcon />,
    //   // disabled: !watchBlock || !watchFloor,
    // },
    {
      name: 'status',
      label: 'Status',
      placeholder: 'Select status',
      type: 'select',
      tabIndex: 10,
      defaultValue: SecurityOfficerState.status || '',
      options: [
        { value: 1, label: 'Active' },
        { value: 2, label: 'Inactive' },
      ],
      rules: { required: 'Status is required' },
      startIcon: <AccessibleOutlined />,
    },
  ];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}-${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}-${date.getFullYear()}`;
  };

  const onSubmit = (data) => {
    const formatData = {
      ...data,
      date_of_birth: formatDate(data.date_of_birth),
    };
    dispatch(updateSecurityOfficer(id, formatData));
  };

  // const handleCancel = () => {
  //   reset();
  // };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    navigate(APP_LINK.LISTECURITYGUARD);
  };

  const handleBack = () => {
    navigate(APP_LINK.LISTECURITYGUARD);
  };
  const navigateToSecurityGuardList = () => {
    navigate(APP_LINK.LISTECURITYGUARD);
  };

  return (
    <Box sx={styles.container}>
      <Paper sx={styles.paper} elevation={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={8} lg={9} xl={10}>
            <Typography variant="h3" sx={styles.heading}>
              Edit Security Officer Details
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={4} lg={3} xl={2} mb={2}>
            <Button
              variant="contained"
              color="error"
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          </Grid>
        </Grid>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <ReusableFormFields
                inputFields={inputFields}
                register={register}
                errors={errors}
                control={control}
                setValue={setValue}
                gridProps={{ xs: 12, md: 6 }}
              />
            </Grid>
          </Grid>

          <Box sx={styles.buttonsContainer}>
            <CustomButton
              type="button"
              text="Cancel"
              variant="contained"
              disableElevation
              size="small"
              onClick={handleBack}
              tabIndex={11}
            />
            <CustomButton
              type="submit"
              text="Edit"
              variant="contained"
              disableElevation
              size="small"
              disabled={securityState?.inProgress}
              loading={securityState?.inProgress}
              tabIndex={12}
            />
          </Box>
        </form>
      </Paper>

      <HospitalDialog
        open={openDialog}
        onClose={handleCloseDialog}
        isSuccess={isSuccess}
        dialogMessage={dialogMessage.message}
        title={isSuccess ? 'Security Update' : 'Error'}
        navigateTo={navigateToSecurityGuardList}
      />
    </Box>
  );
}

export default EditSecurityGuardForm;
